/**
 * HappyHorse 视频生成（经后端 /happyhorse_video → 阿里云百炼 DashScope）
 * 文档：https://help.aliyun.com/zh/model-studio/happyhorse-text-to-video-api-reference
 */

import { resolveApiUrl } from './apiBaseUrl'
import { finalizeVideoPromptForApi } from './seedanceVideoApi'

/** 画布可选模型（含 1.0 / 1.1） */
export const HAPPYHORSE_MODEL_IDS = [
  'happyhorse-1.1-t2v',
  'happyhorse-1.1-i2v',
  'happyhorse-1.1-r2v',
  'happyhorse-1.0-t2v',
  'happyhorse-1.0-i2v',
  'happyhorse-1.0-r2v',
  'happyhorse-1.0-video-edit'
]

export function isHappyHorseVideoModel(modelId) {
  const id = String(modelId != null ? modelId : '')
    .trim()
    .toLowerCase()
  if (!id) return false
  if (HAPPYHORSE_MODEL_IDS.includes(id)) return true
  return /^happyhorse-\d+\.\d+-(t2v|i2v|r2v|video-edit)$/i.test(id)
}

/**
 * 按视频节点模式 + 是否有图，解析实际调用的 HappyHorse 模型 ID。
 * 选中「HappyHorse 1.1」等主条目（*-t2v）时，会随文生/图生/参考自动切换。
 */
export function resolveHappyHorseModelId(selectedModel, opts) {
  const raw = String(selectedModel != null ? selectedModel : '')
    .trim()
    .toLowerCase()
  if (!isHappyHorseVideoModel(raw)) return raw
  if (raw.endsWith('video-edit')) return raw

  const verMatch = raw.match(/^happyhorse-(\d+\.\d+)/)
  const ver = verMatch ? verMatch[1] : '1.1'
  const base = `happyhorse-${ver}`

  const mode = String((opts && opts.mode) || 'ref').trim().toLowerCase()
  const hasImages = !!(opts && opts.hasImages)

  // 用户已显式选中 i2v / r2v 时尊重选择；仅主条目 *-t2v 按模式升级
  const isPrimaryT2v = raw === `${base}-t2v` || raw === `happyhorse-${ver}`
  if (!isPrimaryT2v) return raw

  if (!hasImages || mode === 't2v') return `${base}-t2v`
  if (mode === 'i2v' || mode === 'frames') return `${base}-i2v`
  return `${base}-r2v`
}

function getAuthHeader() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** HappyHorse 仅 720P / 1080P；480p 映射为 720p */
function normalizeHappyHorseResolution(res) {
  const s = res != null ? String(res).trim().toLowerCase() : ''
  if (s === '1080p') return '1080p'
  return '720p'
}

/** 百炼支持的画幅 */
function normalizeHappyHorseRatio(ratio) {
  const r = ratio != null ? String(ratio).trim() : ''
  const allowed = ['16:9', '9:16', '1:1', '4:3', '3:4', '4:5', '5:4', '9:21', '21:9']
  if (allowed.includes(r)) return r
  if (r === 'adaptive' || !r) return '16:9'
  return '16:9'
}

function happyHorseDurationBounds(modelId) {
  const id = String(modelId || '').toLowerCase()
  // 百炼 1.1：3～15 秒；1.0 / API易文档仍兼容 2～15
  if (id.includes('1.1')) return { min: 3, max: 15 }
  return { min: 2, max: 15 }
}

function pickTaskIdFromCreateResponse(data) {
  if (!data || typeof data !== 'object') return ''
  for (const k of ['id', 'task_id', 'taskId']) {
    const v = data[k]
    if (v != null && String(v).trim()) return String(v).trim()
  }
  if (data.data && typeof data.data === 'object') {
    for (const k of ['id', 'task_id', 'taskId']) {
      const v = data.data[k]
      if (v != null && String(v).trim()) return String(v).trim()
    }
  }
  if (data.output && typeof data.output === 'object') {
    const v = data.output.task_id || data.output.taskId
    if (v != null && String(v).trim()) return String(v).trim()
  }
  if (data.raw && typeof data.raw === 'object') {
    return pickTaskIdFromCreateResponse(data.raw)
  }
  return ''
}

function normalizeTaskQueryPayload(data) {
  if (!data || typeof data !== 'object') return data
  // Dingo：{ code, data: { status, video_url, ... } } — 可能多层 data
  let inner = data
  for (let i = 0; i < 5; i++) {
    if (
      inner &&
      typeof inner === 'object' &&
      inner.data &&
      typeof inner.data === 'object' &&
      (inner.status == null ||
        inner.status === '' ||
        (!inner.video_url && !inner.result_url && (inner.data.video_url || inner.data.result_url || inner.data.status)))
    ) {
      inner = { ...inner, ...inner.data }
      continue
    }
    break
  }
  const out = inner.output && typeof inner.output === 'object' ? inner.output : null
  const videoUrl =
    inner.video_url ||
    inner.result_url ||
    inner.video_public_url ||
    inner.upstream_video_url ||
    (out && (out.video_url || out.result_url || (out.results && out.results.video_url))) ||
    ''
  let status = inner.status != null ? inner.status : data.status
  if ((status == null || status === '') && out && out.task_status != null) {
    status = out.task_status
  }
  const id = pickTaskIdFromCreateResponse(inner) || pickTaskIdFromCreateResponse(data)
  return {
    ...inner,
    id: id || inner.id,
    status: status != null ? String(status).toLowerCase() : status,
    video_url: videoUrl || inner.video_url,
    result_url: inner.result_url || videoUrl
  }
}

/**
 * @param {Object} opts
 * @param {string} opts.prompt
 * @param {string} opts.model happyhorse-1.1-t2v | i2v | r2v | 1.0-*
 * @param {number} [opts.duration] 秒
 * @param {string} [opts.resolution] 720p | 1080p
 * @param {string} [opts.ratio] 16:9 等
 * @param {string[]|string} [opts.images] 参考图 / 首帧
 * @param {string} [opts.videoUrl] video-edit 输入视频
 * @param {boolean} [opts.promptExtend]
 * @param {boolean} [opts.watermark]
 * @param {AbortSignal} [opts.signal]
 */
export async function createHappyHorseVideoTask(opts) {
  const {
    prompt,
    model,
    duration,
    resolution,
    ratio,
    images,
    videoUrl,
    promptExtend,
    watermark,
    signal
  } = opts || {}
  const modelId = String(model || 'happyhorse-1.1-t2v').trim()
  if (!isHappyHorseVideoModel(modelId)) {
    throw new Error('不支持的 HappyHorse 模型：' + modelId)
  }
  const imageList = Array.isArray(images)
    ? images.map(x => (x == null ? '' : String(x).trim())).filter(Boolean)
    : images
      ? [String(images).trim()].filter(Boolean)
      : []
  const bounds = happyHorseDurationBounds(modelId)
  const durRaw =
    duration != null && !Number.isNaN(Number(duration)) ? Math.round(Number(duration)) : 5
  const dur = Math.min(bounds.max, Math.max(bounds.min, durRaw))
  const res = await fetch(resolveApiUrl('/happyhorse_video'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify({
      prompt: finalizeVideoPromptForApi(prompt),
      model: modelId,
      duration: dur,
      resolution: normalizeHappyHorseResolution(resolution),
      ratio: normalizeHappyHorseRatio(ratio),
      ...(imageList.length ? { images: imageList } : {}),
      ...(videoUrl ? { video_url: String(videoUrl).trim() } : {}),
      ...(promptExtend != null ? { prompt_extend: !!promptExtend } : {}),
      ...(watermark != null ? { watermark: !!watermark } : {})
    }),
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || { raw: text }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.errors)) ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  const taskId = pickTaskIdFromCreateResponse(data)
  if (!taskId) {
    throw new Error(
      '创建 HappyHorse 任务成功但未解析到 task_id。响应片段：' + text.slice(0, 400)
    )
  }
  return { ...data, id: taskId }
}

export async function getHappyHorseVideoTask(taskId, signal) {
  const id = encodeURIComponent(String(taskId || '').trim())
  if (!id) throw new Error('缺少 task_id')
  const res = await fetch(resolveApiUrl(`/happyhorse_video/${id}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || {}
  if (!res.ok) {
    const msg = data.message || data.error || text.slice(0, 400) || `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return normalizeTaskQueryPayload(data)
}

export function pickHappyHorseTaskVideoUrl(info) {
  if (!info || typeof info !== 'object') return ''
  const u =
    info.video_url ||
    info.result_url ||
    info.video_public_url ||
    info.upstream_video_url ||
    (info.output &&
      (info.output.video_url ||
        info.output.result_url ||
        (info.output.results && info.output.results.video_url))) ||
    (info.data &&
      (info.data.video_url || info.data.result_url || info.data.video_public_url)) ||
    ''
  return String(u || '').trim()
}
